/**
 * DDL and statement-head positions that the clause machine has no state for.
 *
 * Each of these carets used to fall through to the resolver's full emission —
 * `CREATE TABLE t (id INT ▮` and `ALTER TABLE t ADD ▮` offered every table and
 * schema in the database, `EXPLAIN (▮` and `BEGIN ▮` offered the whole keyword
 * list — when the legal continuation is a short closed set in every case.
 *
 * Same CONFIDENT-ONLY contract as the other shape classifiers: `null` unless
 * the scan is clearly at the named boundary.
 */

import type { NextKeywordSlot } from './legalNextKeywords';
import { parenHeadScanEnd } from './clauseShapeSlot';
import { scanRegion, slotScanEnd, wordBefore, type ScanOptions } from './slotTextScan';
import { isInsideDollarQuotedBody } from './insideRoutineBody';

export function classifyDdlShapeSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  return (
    classifyStatementIntroducerSlot(text, offset, statementStart, opts) ??
    classifyExplainOptionSlot(text, offset, statementStart, opts) ??
    classifyColumnConstraintSlot(text, offset, statementStart, opts)
  );
}

/** `CREATE VIEW v ▮`, `ALTER TABLE t ADD ▮`, `BEGIN ▮`, `LOCK TABLES t ▮`,
 *  `SELECT … INTO ▮` — top-level shapes read from the statement's leading
 *  words. All are outside any paren. */
function classifyStatementIntroducerSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  if (scan.openParenAt !== null) return null;
  const words = scan.tokens.map((t) => t.word);
  if (words.length === 0) return null;
  const last = words[words.length - 1]!;

  // `BEGIN ▮` — only as the whole statement. A `BEGIN` that opens a procedural
  // block or follows anything else is a different construct. Inside a
  // dollar-quoted PL/pgSQL body (`… AS $$ DECLARE … BEGIN ▮`) the statement
  // slicer hands us `BEGIN` alone too, but there it opens the block — the
  // transaction modes would be the wrong answer, so decline and let the full
  // emission (locals + statement keywords) run.
  if (words.length === 1 && last === 'BEGIN') {
    if (opts.dollarQuotedStrings && isInsideDollarQuotedBody(text, statementStart)) return null;
    return 'after-begin';
  }

  // `CREATE [OR REPLACE] VIEW name ▮` — the name is the last word and `VIEW`
  // the one before it, so a still-unnamed `CREATE VIEW ▮` does not qualify.
  if (words[0] === 'CREATE' && words[words.length - 2] === 'VIEW' && last !== 'VIEW') {
    return 'after-create-view-name';
  }

  // `ALTER TABLE t ADD ▮` — `ADD COLUMN ▮` names the column, so stop at ADD.
  if (words[0] === 'ALTER' && words[1] === 'TABLE' && last === 'ADD') {
    return 'after-alter-table-add';
  }

  // `LOCK TABLES t ▮` — the relation is named, the mode is owed.
  if (words[0] === 'LOCK' && words[1] === 'TABLES' && words.length >= 3 && last !== 'TABLES') {
    return 'after-lock-tables-relation';
  }

  // `SELECT … INTO ▮` — MySQL's destination. Only when a FROM has already been
  // written, so T-SQL's `SELECT … INTO newtable` (a relation position) and
  // `INSERT INTO` are not caught.
  if (words[0] === 'SELECT' && last === 'INTO' && words.includes('FROM')) {
    return 'after-select-into';
  }
  return null;
}

/** `EXPLAIN (▮` / `EXPLAIN (ANALYZE, ▮` — Postgres' option list. */
function classifyExplainOptionSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = parenHeadScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return null;
  if (wordBefore(text, openAt, statementStart) !== 'EXPLAIN') return null;
  // Only at the head of an option — mid-option (`FORMAT ▮`) takes a value, and
  // only the empty list or a fresh comma-separated slot starts a new one.
  const optionList = text.slice(openAt + 1, scanEnd);
  return optionList.trim() === '' || /,\s*$/.test(optionList) ? 'at-explain-option' : null;
}

/** `CREATE TABLE t (id INT ▮` — the column's constraints.
 *
 *  Confident only at the SIMPLE shape: the caret follows a bare word that
 *  itself follows the column name, at depth 1 of a `CREATE TABLE`'s list. A
 *  parameterised type (`NUMERIC(10, 2) ▮`) closes its own paren and is handled
 *  the same way; a caret INSIDE those parens is a length, not a constraint. */
function classifyColumnConstraintSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const openAt = scan.openParenAt;
  if (openAt === null) return null;
  const words = scan.tokens.map((t) => t.word);
  if (words[0] !== 'CREATE' || !words.includes('TABLE')) return null;
  // The open paren must be the column list's — i.e. the word before it is the
  // table name, not another construct (`CHECK (`, `REFERENCES t (`).
  const beforeParen = wordBefore(text, openAt, statementStart);
  if (CONSTRAINT_PAREN_OWNERS.has(beforeParen)) return null;

  // Walk back to this column definition's start — the list's last top-level
  // comma, which the bareword scan cannot see.
  const listText = text.slice(openAt + 1, scanEnd);
  const lastComma = lastTopLevelComma(listText);
  // A parameterised type is ONE word: `amt NUMERIC(10, 2) ▮` is the same shape
  // as `id INT ▮`, so its argument list is removed before counting.
  const definition = stripNestedGroups(listText.slice(lastComma + 1))
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  // `name TYPE ▮` — exactly two words so far, and the first must be a column
  // name rather than a table-level constraint opener.
  if (definition.length !== 2) return null;
  return CONSTRAINT_LEADS.has(definition[0]!.toUpperCase()) ? null : 'after-column-data-type';
}

/** Words whose `(` is NOT a CREATE TABLE column list. */
const CONSTRAINT_PAREN_OWNERS: ReadonlySet<string> = new Set([
  'CHECK', 'REFERENCES', 'KEY', 'UNIQUE', 'EXCLUDE', 'INDEX', 'VALUES', 'IN', 'ON',
]);

/** Table-level constraint openers — a definition starting with one of these is
 *  not a column definition at all. */
const CONSTRAINT_LEADS: ReadonlySet<string> = new Set([
  'PRIMARY', 'UNIQUE', 'FOREIGN', 'CONSTRAINT', 'CHECK', 'EXCLUDE', 'FULLTEXT',
  'SPATIAL', 'INDEX', 'KEY', 'PERIOD', 'LIKE',
]);

/** `s` with every nested `( … )` group removed, so a parameterised type counts
 *  as a single word. */
function stripNestedGroups(s: string): string {
  let out = '';
  let depth = 0;
  for (const c of s) {
    if (c === '(') depth += 1;
    else if (c === ')') depth -= 1;
    else if (depth === 0) out += c;
  }
  return out;
}

/** Index of the last comma at paren depth 0 in `s`, or -1. */
function lastTopLevelComma(s: string): number {
  let depth = 0;
  let found = -1;
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (c === '(') depth += 1;
    else if (c === ')') depth -= 1;
    else if (c === ',' && depth === 0) found = i;
  }
  return found;
}
